import CreditCardIcon from '@mui/icons-material/CreditCard';
import PixIcon from '@mui/icons-material/Pix';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Stack } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import { useMutationCallbacks } from '@woovi/relay';
import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { ActionButton } from '@woovi/ui';

import type { PaymentMethodSelectedPending_charge$key } from './__generated__/PaymentMethodSelectedPending_charge.graphql.ts';
import type { ChargeSelectPaymentMethodMutation } from './activeWaitingPaymentMethod/mutations/__generated__/ChargeSelectPaymentMethodMutation.graphql.ts';
import { ChargeSelectPaymentMethod } from './activeWaitingPaymentMethod/mutations/ChargeSelectPaymentMethodMutation.tsx';
import PaymentMethodPaymentDeadline from './paymentMethod/PaymentMethodPaymentDeadline.tsx';
import PaymentMethodPendingMessage from './paymentMethod/PaymentMethodPendingMessage.tsx';
import PaymentMethodSelectableCard from './paymentMethod/PaymentMethodSelectableCard.tsx';
import PaymentMethodTransactionDetails from './paymentMethod/PaymentMethodTransactionDetails.tsx';

type PaymentMethodSelectedPendingProps = {
  charge: PaymentMethodSelectedPending_charge$key;
};

const PaymentMethodSelectedPending = (
  props: PaymentMethodSelectedPendingProps,
): ReactNode => {
  const { t } = useTranslation();
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);

  const charge = useFragment<PaymentMethodSelectedPending_charge$key>(
    graphql`
      fragment PaymentMethodSelectedPending_charge on Charge {
        id
        availablePaymentMethods
        ...PaymentMethodPendingMessage_charge
        ...PaymentMethodTransactionDetails_charge
        ...PaymentMethodPaymentDeadline_charge
      }
    `,
    props.charge,
  );

  const [chargeSelectPaymentMethod, isPending] =
    useMutationCallbacks<ChargeSelectPaymentMethodMutation>({
      name: 'ChargeSelectPaymentMethod',
      mutation: ChargeSelectPaymentMethod,
    });

  const handleChargeSelectPaymentMethod = async (): Promise<void> => {
    if (!paymentMethod) {
      return;
    }

    chargeSelectPaymentMethod({
      variables: {
        input: {
          chargeId: charge.id,
          paymentMethod,
        },
      },
    });
  };

  const hasPaymentMethodBoleto = charge.availablePaymentMethods?.includes(
    PAYMENT_METHOD_TYPE.BOLETO,
  );

  const hasPaymentMethodCreditCard = charge.availablePaymentMethods?.includes(
    PAYMENT_METHOD_TYPE.CREDIT_CARD,
  );

  const hasPaymentMethodPix = charge.availablePaymentMethods?.includes(
    PAYMENT_METHOD_TYPE.PIX,
  );

  const handlePaymentMethodSelect = (value: string): void => {
    setPaymentMethod(value);
  };

  return (
    <Stack
      sx={{
        width: '100%',
        alignItems: 'center',
        gap: 2,
        '@media(max-width: 650px)': {
          '& > * ': {
            maxWidth: '300px',
          },
        },
      }}
    >
      <PaymentMethodPendingMessage charge={charge} />
      <Stack
        sx={{
          width: '100%',
          gap: 1,
          '@media(min-width: 650px)': {
            px: 5,
          },
        }}
      >
        {hasPaymentMethodPix && (
          <PaymentMethodSelectableCard
            isSelected={paymentMethod === PAYMENT_METHOD_TYPE.PIX}
            value={PAYMENT_METHOD_TYPE.PIX}
            label={t('Pix')}
            onClick={handlePaymentMethodSelect}
            icon={<PixIcon />}
            disabled={isPending}
          />
        )}
        {hasPaymentMethodBoleto && (
          <PaymentMethodSelectableCard
            isSelected={paymentMethod === PAYMENT_METHOD_TYPE.BOLETO}
            value={PAYMENT_METHOD_TYPE.BOLETO}
            label={t('Boleto')}
            onClick={handlePaymentMethodSelect}
            icon={<ReceiptLongIcon />}
            disabled={isPending}
          />
        )}
        {hasPaymentMethodCreditCard && (
          <PaymentMethodSelectableCard
            isSelected={paymentMethod === PAYMENT_METHOD_TYPE.CREDIT_CARD}
            value={PAYMENT_METHOD_TYPE.CREDIT_CARD}
            label={t('Credit Card')}
            onClick={handlePaymentMethodSelect}
            icon={<CreditCardIcon />}
            disabled={isPending}
          />
        )}
        <ActionButton
          onClick={handleChargeSelectPaymentMethod}
          disabled={isPending || !paymentMethod}
          loading={isPending}
          fullWidth
        >
          {t('Continue')}
        </ActionButton>
      </Stack>
      <Stack sx={{ gap: 1 }}>
        <PaymentMethodPaymentDeadline charge={charge} />
        <PaymentMethodTransactionDetails charge={charge} />
      </Stack>
    </Stack>
  );
};

export default PaymentMethodSelectedPending;
